// Sélecteur 1 set / 2 achetés + 1 offert (Ongles Lab, 06/10/2026).
// En mode 2+1 : intercepte l'ajout au panier du formulaire produit et ajoute les 3 sets choisis ;
// la remise automatique « 2 + 1 offert » (collection kits-faux-ongles) fait le reste.
// 07/10/2026 : formules lues dans le HTML (data-count / data-free de chaque option) → grande offre « 3 achetés + 2 offerts »
// réglable dans le bloc ; contour autour de toute l'offre (les cases des modèles sont déplacées dans l'option choisie) ;
// 2e photo (capsule détourée) qui glisse dans la fenêtre de choix.
import { CartUpdatedEvent } from '@theme/events';

const eur = (c) => (c / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
let dataPromise = null;
// Libellé de forme comme sur le site : Extra-court, Carré court, Amande court, Amande, Coffin
const shape = (x) => (/^extra/i.test(x.f || '') ? 'Extra-court' : x.l === 'Court' ? `${x.f} court` : x.f || '');

class OlBundle extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.current = JSON.parse(this.dataset.current);
    // À l'arrivée : seul le set 1 est rempli (le set de la page), 2 et 3 sont à choisir
    this.slotEls = [...this.querySelectorAll('[data-slot]')];
    this.slots = this.slotEls.map((_, i) => (i === 0 ? this.current : null));
    this.slots.forEach((_, i) => this.renderSlot(i));
    this.slotsBox = this.querySelector('[data-ref="slots"]');
    this.cartReady = this.dataset.cartReady === '1';
    this.activeSlot = 0;
    this.dialog = this.querySelector('[data-ref="dialog"]');
    this.grid = this.querySelector('[data-ref="grid"]');
    this.form = this.closest('product-form')?.querySelector('form');

    this.querySelectorAll('input[type="radio"]').forEach((r) => r.addEventListener('change', () => this.setMode(r.value)));
    // Un clic n'importe où dans l'encadré d'une offre (lignes avantages comprises) la sélectionne, sauf dans les cases des modèles
    this.querySelectorAll('div[data-opt]').forEach((o) =>
      o.addEventListener('click', (e) => {
        if (e.target.closest('[data-ref="slots"]') || e.target.closest('label')) return;
        const r = o.querySelector('input[type="radio"]');
        if (r && !r.checked) { r.checked = true; this.setMode(r.value); }
      })
    );
    this.querySelectorAll('[data-slot]').forEach((b) =>
      b.addEventListener('click', () => this.openPicker(parseInt(b.dataset.slot, 10)))
    );
    // Petite croix : vide la case, on peut recliquer pour choisir un autre modèle
    this.querySelectorAll('[data-remove]').forEach((x) => {
      const rm = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const i = parseInt(x.closest('[data-slot]').dataset.slot, 10);
        this.slots[i] = null;
        this.renderSlot(i);
        this.updatePrices();
      };
      x.addEventListener('click', rm);
      x.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') rm(e); });
    });
    this.querySelector('[data-ref="close"]').addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
    this.querySelectorAll('[data-filter]').forEach((s) => s.addEventListener('change', () => this.renderGrid()));

    // Avant le gestionnaire du thème (phase de capture sur la cible)
    this.onSubmit = this.onSubmit.bind(this);
    this.form?.addEventListener('submit', this.onSubmit, true);

    let def = this.dataset.defaultOption || '1';
    if (!this.querySelector(`input[value="${def}"]`)) def = '1';
    const radio = this.querySelector(`input[value="${def}"]`);
    if (radio) radio.checked = true;
    this.setMode(def);
    // Le bouton d'ajout au panier est rendu après ce bloc
    requestAnimationFrame(() => this.updatePrices());
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 1200));
    idle(() => this.preload(), { timeout: 3000 });
    this.querySelectorAll('[data-slot]').forEach((b) => b.addEventListener('pointerenter', () => this.load(), { once: true }));
    window.addEventListener('load', () => this.updatePrices(), { once: true });
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit, true);
  }

  setMode(mode) {
    this.mode = mode;
    const opt = this.querySelector(`[data-opt="${mode}"]`);
    this.count = parseInt(opt?.dataset.count || '1', 10);
    this.free = parseInt(opt?.dataset.free || '0', 10);
    this.extra = parseInt(opt?.dataset.extra || '0', 10);
    this.classList.toggle('is-offer', this.count > 1);
    this.querySelectorAll('[data-opt]').forEach((o) => o.classList.toggle('is-selected', o === opt));
    // Les cases des modèles vont dans l'encadré de l'offre choisie → le contour noir entoure tout
    if (this.count > 1 && opt && this.slotsBox.parentElement !== opt) opt.appendChild(this.slotsBox);
    this.slotEls.forEach((b, i) => {
      b.hidden = i >= this.count;
      const isFree = i >= this.count - this.free && i < this.count;
      b.classList.toggle('ol-bundle__slot--free', isFree);
    });
    const cnt = this.querySelector('[data-ref="count"]');
    if (cnt) cnt.textContent = String(this.count);
    this.updatePrices();
  }

  active() {
    return this.slots.slice(0, this.count || 1);
  }

  isComplete() {
    return this.active().every(Boolean);
  }

  renderSlot(i) {
    const s = this.slots[i];
    const b = this.querySelector(`[data-slot="${i}"]`);
    if (!b) return;
    b.classList.toggle('is-empty', !s);
    if (!s) return;
    b.querySelector('.ol-bundle__slot-img').src = s.img;
    b.querySelector('.ol-bundle__slot-name').textContent = s.t;
    b.querySelector('.ol-bundle__slot-meta').textContent = shape(s);
  }

  // Prix d'une formule : n sets, les « free » moins chers offerts. Case vide : prix du set de la page.
  // extra = valeur des cadeaux inclus (sérum, cadeau mystère si valorisé) : ajoutée au prix barré et à l'économie, jamais au prix payé
  totals(count = this.count || 1, free = this.free || 0, extra = this.extra || 0) {
    const p = this.slots.slice(0, count).map((s) => (s ? s.p : this.current.p));
    while (p.length < count) p.push(this.current.p);
    const all = p.reduce((a, b) => a + b, 0);
    const save = [...p].sort((a, b) => a - b).slice(0, free).reduce((a, b) => a + b, 0);
    return { all: all + extra, total: all - save, save: save + extra };
  }

  updatePrices() {
    const { all, total } = this.totals();
    this.querySelectorAll('div[data-opt]').forEach((o) => {
      const n = parseInt(o.dataset.count, 10);
      const t = this.totals(n, parseInt(o.dataset.free, 10), parseInt(o.dataset.extra || '0', 10));
      const set = (ref, v) => { const el = o.querySelector(`.ol-bundle__row [data-ref="${ref}"]`); if (el) el.textContent = v; };
      set('total', eur(t.total));
      set('was', eur(t.all));
      set('per', `soit ${Math.round(t.total / n / 100)} € / set`); // arrondi à l'euro le plus proche (Valentin 07/10)
      set('save', `Économisez ${eur(t.save)}`);
    });
    // Prix affiché dans le bouton d'ajout au panier
    const btn = this.closest('product-form')?.querySelector('[data-ref="add-to-cart-button-container"]');
    if (!btn) return;
    const price = btn.querySelector('[data-ref="price"]');
    const was = btn.querySelector('[data-ref="compare-at-price"]');
    const label = btn.querySelector('.full-add-to-cart-text');
    const addBtn = btn.querySelector('[data-ref="add-to-cart-button"]');
    if (label && !label.dataset.olLabel) label.dataset.olLabel = label.textContent.trim();
    const multi = this.count > 1;
    const incomplete = multi && !this.isComplete();
    // Grande offre sans remise correspondante : affichage seul, ajout au panier bloqué
    const blocked = this.mode === 'big' && !this.cartReady;
    this.classList.toggle('is-incomplete', incomplete);
    if (addBtn) addBtn.disabled = blocked;
    const missing = this.active().filter((s) => !s).length;
    const msg = missing === 1 ? 'Choisissez votre dernier modèle' : `Choisissez vos ${missing} autres modèles`;
    const gain = this.free > 1 ? `de vos ${this.free} sets offerts` : `du ${this.count}e offert`;
    const need = this.querySelector('[data-ref="need"]');
    if (need) need.textContent = missing === 1 ? `Plus qu’un modèle à choisir pour profiter ${gain}.` : `Choisissez vos ${missing} autres modèles pour profiter ${gain}.`;
    btn.classList.toggle('ol-bundle-incomplete', incomplete || blocked);
    if (label) label.textContent = blocked ? 'Offre bientôt disponible' : incomplete ? msg : label.dataset.olLabel;
    if (multi) {
      if (price) price.textContent = eur(total);
      if (was) was.textContent = eur(all);
    } else {
      if (price) price.textContent = eur(this.current.p);
      if (was) was.textContent = '';
    }
  }

  // Données des sets : cache de session (30 min) + préchargement à l'inactivité de la page
  async load() {
    if (!dataPromise) {
      const KEY = 'ol-bundle-data-v4';
      try {
        const c = JSON.parse(sessionStorage.getItem(KEY) || 'null');
        if (c && Date.now() - c.t < 30 * 60 * 1000 && Array.isArray(c.d)) dataPromise = Promise.resolve(c.d);
      } catch (e) {}
      if (!dataPromise) {
        dataPromise = fetch(this.dataset.dataUrl, { credentials: 'same-origin' })
          .then((r) => r.text())
          .then((t) => {
            const d = JSON.parse(t.trim());
            try { sessionStorage.setItem(KEY, JSON.stringify({ t: Date.now(), d })); } catch (e) {}
            return d;
          })
          .catch(() => { dataPromise = null; return []; });
      }
    }
    return dataPromise;
  }

  preload() {
    this.load().then((d) => {
      // Précharge les premières vignettes pour une ouverture immédiate
      d.slice(0, 12).forEach((x) => { const i = new Image(); i.decoding = 'async'; i.src = x.img; });
      d.slice(0, 4).forEach((x) => { if (x.i2) { const i = new Image(); i.decoding = 'async'; i.src = x.i2; } });
    });
  }

  async openPicker(i) {
    this.activeSlot = i;
    this.querySelector('[data-ref="slotnum"]').textContent = i >= this.count - this.free ? `${i + 1} (offert)` : String(i + 1);
    this.dialog.showModal();
    // Emma 06/10 : Safari entourait la croix en bleu (focus auto sur le 1er bouton) → on place le focus sur la fenêtre elle-même
    const sheet = this.dialog.querySelector('.ol-bundle__sheet');
    if (sheet) { sheet.tabIndex = -1; sheet.focus({ preventScroll: true }); }
    this.items = await this.load();
    if (!this.items.some((x) => x.id === this.current.id)) this.items.unshift(this.current);
    this.fillFilters();
    this.renderGrid();
  }

  fillFilters() {
    if (this._filtersDone) return;
    this._filtersDone = true;
    const order = { f: ['Extra-Courte', 'Carré', 'Amande', 'Coffin'], l: ['Extra-Court', 'Court', 'Long'] };
    this.querySelectorAll('[data-filter]').forEach((sel) => {
      const k = sel.dataset.filter;
      let vals = [...new Set(this.items.map((x) => x[k]).filter(Boolean))];
      vals.sort((a, b) => (order[k] ? order[k].indexOf(a) - order[k].indexOf(b) : a.localeCompare(b, 'fr')));
      vals.forEach((v) => sel.insertAdjacentHTML('beforeend', `<option value="${v}">${v}</option>`));
    });
  }

  renderGrid() {
    if (!this.items) return;
    const f = {};
    this.querySelectorAll('[data-filter]').forEach((s) => (f[s.dataset.filter] = s.value));
    const list = this.items.filter((x) => (!f.c || x.c === f.c) && (!f.f || x.f === f.f) && (!f.l || x.l === f.l));
    const sel = this.slots[this.activeSlot]?.id;
    this.grid.innerHTML = list.length
      ? list
          .map(
            (x) => `<button type="button" class="ol-bundle__card${x.id === sel ? ' is-selected' : ''}" data-id="${x.id}">
              <span class="ol-bundle__media">${x.i2
                ? `<span class="ol-bundle__gal" data-gal><span class="ol-bundle__gal-track"><img src="${x.img}" alt="" loading="lazy" decoding="async" width="360" height="360"><img data-src="${x.i2}" alt="" decoding="async" width="360" height="360"></span><span class="ol-bundle__dots" aria-hidden="true"><i class="is-on"></i><i></i></span></span>`
                : `<img src="${x.img}" alt="" loading="lazy" decoding="async" width="360" height="360">`}<span class="ol-bundle__zoom" data-zoom role="button" tabindex="0" aria-label="Voir en grand"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.5 15.5L21 21"/></svg></span></span>
              <span class="ol-bundle__card-name">${x.t}</span>
              <span class="ol-bundle__card-meta">${shape(x)}</span>
            </button>`
          )
          .join('')
      : '<p class="ol-bundle__loading">Aucun modèle avec ces filtres.</p>';
    this.grid.querySelectorAll('[data-id]').forEach((b) =>
      b.addEventListener('click', () => this.choose(this.items.find((x) => String(x.id) === b.dataset.id)))
    );
    // Photo capsule chargée dès que la vignette approche de l'écran (pas au premier glissement)
    this._io?.disconnect();
    this._io = 'IntersectionObserver' in window
      ? new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target._wake?.(); this._io.unobserve(e.target); } }), { root: this.grid, rootMargin: '300px 0px' })
      : null;
    this.grid.querySelectorAll('[data-gal]').forEach((g) => { this.initGallery(g); this._io ? this._io.observe(g) : g._wake(); });
    // Loupe : aperçu en grand, sans sélectionner le modèle (Valentin 07/10)
    this.grid.querySelectorAll('[data-zoom]').forEach((z) => {
      const open = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openQuickView(this.items.find((x) => String(x.id) === z.closest('[data-id]').dataset.id));
      };
      z.addEventListener('click', open);
      z.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') open(e); });
      z.addEventListener('touchstart', (e) => e.stopPropagation(), { passive: true });
    });
  }

  // Aperçu en grand : photo portée + capsule (720 px, chargées seulement à l'ouverture), titre et forme
  openQuickView(x) {
    if (!x) return;
    const big = (u) => (u || '').replace(/width=\d+/, 'width=720');
    if (!this.qv) {
      this.qv = document.createElement('dialog');
      this.qv.className = 'ol-bundle__qv';
      this.qv.innerHTML = `<div class="ol-bundle__qv-box">
          <button type="button" class="ol-bundle__qv-close" aria-label="Fermer">×</button>
          <div class="ol-bundle__qv-media"></div>
          <p class="ol-bundle__qv-title"></p>
          <p class="ol-bundle__qv-meta"></p>
        </div>`;
      this.dialog.appendChild(this.qv);
      this.qv.querySelector('.ol-bundle__qv-close').addEventListener('click', () => this.qv.close());
      this.qv.addEventListener('click', (e) => { if (e.target === this.qv) this.qv.close(); });
      // Le clic dans l'aperçu ne doit pas remonter jusqu'à la fenêtre de choix (qui se fermerait)
      this.qv.addEventListener('click', (e) => e.stopPropagation());
    }
    const media = this.qv.querySelector('.ol-bundle__qv-media');
    const arrows = '<button type="button" class="ol-bundle__qv-arrow ol-bundle__qv-arrow--prev" data-go="0" aria-label="Photo précédente">‹</button><button type="button" class="ol-bundle__qv-arrow ol-bundle__qv-arrow--next" data-go="1" aria-label="Photo suivante">›</button>';
    media.innerHTML = x.i2
      ? `<span class="ol-bundle__gal" data-gal><span class="ol-bundle__gal-track"><img src="${big(x.img)}" alt="${x.t}" decoding="async" width="720" height="720"><img data-src="${big(x.i2)}" alt="${x.t} : capsule" decoding="async" width="720" height="720"></span><span class="ol-bundle__dots" aria-hidden="true"><i class="is-on"></i><i></i></span>${arrows}</span>`
      : `<img src="${big(x.img)}" alt="${x.t}" decoding="async" width="720" height="720">`;
    this.qv.querySelector('.ol-bundle__qv-title').textContent = x.t;
    this.qv.querySelector('.ol-bundle__qv-meta').textContent = shape(x);
    const g = media.querySelector('[data-gal]');
    if (g) {
      this.initGallery(g, false);
      g._wake();
      g.querySelectorAll('[data-go]').forEach((a) => a.addEventListener('click', (e) => { e.stopPropagation(); g._go(parseInt(a.dataset.go, 10)); }));
    }
    this.qv.showModal();
    const box = this.qv.querySelector('.ol-bundle__qv-box');
    box.tabIndex = -1;
    box.focus({ preventScroll: true });
  }

  // Mini-galerie d'une vignette : 2 photos sur une piste à 200 % décalée par translate (−50 % pile), pas de défilement natif
  // → plus de liseré de la photo précédente (arrondis de scrollLeft). Glisser au doigt, survol à la souris.
  initGallery(g, hover = true) {
    const track = g.firstElementChild;
    const second = track.children[1];
    const dots = g.querySelectorAll('.ol-bundle__dots i');
    g._wake = () => { if (second.dataset.src) { second.src = second.dataset.src; delete second.dataset.src; } };
    let idx = 0, x0 = 0, y0 = 0, dx = 0, drag = null, moved = false;
    const go = (i) => {
      idx = i;
      g._wake();
      track.style.transform = '';
      track.classList.toggle('is-2', i === 1);
      dots.forEach((d, j) => d.classList.toggle('is-on', j === i));
      g.classList.toggle('is-2', i === 1);
    };
    g._go = go;
    g.addEventListener('touchstart', (e) => {
      g._wake();
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; dx = 0; drag = null; moved = false;
    }, { passive: true });
    g.addEventListener('touchmove', (e) => {
      const t = e.touches[0];
      dx = t.clientX - x0;
      const dy = t.clientY - y0;
      if (drag === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) drag = Math.abs(dx) > Math.abs(dy);
      if (!drag) return;
      e.preventDefault();
      moved = true;
      const w = g.clientWidth || 1;
      const off = Math.max(-w, Math.min(0, -idx * w + dx));
      track.classList.add('is-drag');
      track.style.transform = `translate3d(${(off / w) * 50}%,0,0)`;
    }, { passive: false });
    g.addEventListener('touchend', () => {
      track.classList.remove('is-drag');
      if (drag) go(Math.abs(dx) > g.clientWidth * 0.18 ? (dx < 0 ? 1 : 0) : idx);
      drag = null;
    });
    // Un glissement ne doit pas sélectionner le modèle
    g.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } });
    if (hover && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      const b = g.closest('button');
      b.addEventListener('mouseenter', () => go(1));
      b.addEventListener('mouseleave', () => go(0));
    }
  }

  choose(item) {
    if (!item) return;
    this.slots[this.activeSlot] = item;
    this.renderSlot(this.activeSlot);
    this.dialog.close();
    this.updatePrices();
  }

  async onSubmit(event) {
    if (!(this.count > 1)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.mode === 'big' && !this.cartReady) return;
    if (!this.isComplete()) {
      this.openPicker(this.active().findIndex((s) => !s));
      return;
    }
    const btn = this.closest('product-form')?.querySelector('[data-ref="add-to-cart-button"]');
    btn?.classList.add('is-loading');
    const byId = new Map();
    this.active().forEach((s) => byId.set(s.id, (byId.get(s.id) || 0) + 1));
    const items = [...byId].map(([id, quantity]) => ({ id, quantity }));
    const sections = ['auto-gifts'];
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.dataset.sectionId) sections.push(drawer.dataset.sectionId);
    try {
      const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Erreur');
      const count = document.querySelector('cart-icon')?.getCurrentItemCount?.();
      document.dispatchEvent(
        new CartUpdatedEvent({}, { itemCount: count != null ? count + this.count : undefined, source: 'product-form', sections: data.sections })
      );
      setTimeout(() => {
        if (drawer && !drawer.hasAttribute('open') && !drawer.classList.contains('is-open')) document.dispatchEvent(new Event('cart:open'));
      }, 150);
    } catch (e) {
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: e.message } }));
    } finally {
      btn?.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('ol-bundle')) customElements.define('ol-bundle', OlBundle);
